import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Container, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import * as chatApi from '../api/chat';
import { useAuth } from '../contexts/AuthContext';
import { countActivePaidFeatures } from '../utils/premiumAccess';
import ChatSettingsPanel from '../components/ChatSettingsPanel';
import { formatChatMarkdown, hasLatexDelimiters } from '../components/ChatWidget/formatChatMarkdown';

interface ChatMessage {
  from: 'user' | 'bot';
  text: string;
  html?: string;
  imageDataUrl?: string;
}

const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Could not read image'));
  reader.onerror = () => reject(new Error('Could not read image'));
  reader.readAsDataURL(file);
});

declare global {
  interface Window {
    MathJax: any;
  }
}

const ChatRoom: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const activePaidFeatureCount = countActivePaidFeatures(user?.paidFeatures, user?.paidFeatureExpiries);
  const hasChatAccess = user?.role === 'admin' || activePaidFeatureCount >= 2;
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<{ file: File; dataUrl: string } | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    { from: 'bot', text: 'Hi! I am Einstein. Ask me anything about your learning, the A3 website, or our research.' },
  ]);
  const [userId] = useState(() => {
    const key = 'einstein-chat-user-id';
    let id = localStorage.getItem(key);
    if (!id) {
      id = `user-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem(key, id);
    }
    return id;
  });
  const [conversationId] = useState(() => `conv-${Date.now()}`);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const mathJaxLoading = useRef<Promise<void> | null>(null);
  const requestController = useRef<AbortController | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!messages.some((message) => message.html && hasLatexDelimiters(message.html))) return;

    const typeset = async () => {
      if (!window.MathJax?.typesetPromise) {
        if (!mathJaxLoading.current) {
          window.MathJax = {
            tex: { inlineMath: [['$', '$'], ['\\(', '\\)']], displayMath: [['$$', '$$'], ['\\[', '\\]']] },
            startup: { typeset: false },
          };
          mathJaxLoading.current = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'einstein-mathjax-full-page';
            script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('MathJax failed to load'));
            document.head.appendChild(script);
          });
        }
        await mathJaxLoading.current;
      }
      await window.MathJax?.typesetPromise?.();
    };
    typeset().catch(() => undefined);
  }, [messages]);

  const handleImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setAttachmentError('');
    if (!file) return;
    if (!IMAGE_MIME_TYPES.has(file.type)) {
      setAttachmentError('Choose a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      setAttachmentError('Images must be 5 MB or smaller.');
      return;
    }
    try {
      setSelectedImage({ file, dataUrl: await fileToDataUrl(file) });
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : 'Could not read image.');
    }
  };

  const stopResponse = () => requestController.current?.abort();

  const send = async (editedText?: string, editedIndex?: number) => {
    const text = (editedText ?? input).trim();
    if ((!text && !selectedImage) || sending) return;
    const targetEditIndex = typeof editedIndex === 'number' ? editedIndex : editingIndex ?? undefined;
    const currentImage = selectedImage;
    const userMessage: ChatMessage = {
      from: 'user',
      text,
      ...(currentImage ? { imageDataUrl: currentImage.dataUrl } : {}),
    };
    if (typeof targetEditIndex === 'number') {
      setMessages((current) => [...current.slice(0, targetEditIndex), userMessage]);
      setEditingIndex(null);
    } else {
      setMessages((current) => [...current, userMessage]);
    }
    setInput('');
    setSelectedImage(null);
    setSending(true);
    const controller = new AbortController();
    requestController.current = controller;
    try {
      const images = currentImage ? [{ mimeType: currentImage.file.type, data: currentImage.dataUrl.split(',')[1] || '' }] : undefined;
      const prompt = text || 'Please analyze the attached image.';
      const editFromTurn = typeof targetEditIndex === 'number' ? Math.max(0, Math.floor((targetEditIndex - 1) / 2)) : undefined;
      const response = await chatApi.sendMessage(prompt, conversationId, userId, undefined, images, controller.signal, editFromTurn);
      if (response?.cancelled || controller.signal.aborted) {
        setInput(text);
        setEditingIndex(targetEditIndex ?? null);
        return;
      }
      const reply = String(response?.reply || response?.message || response?.data || 'Einstein could not provide a response.');
      setMessages((current) => [...current, { from: 'bot', text: reply, html: formatChatMarkdown(reply) }]);
    } catch (error) {
      if (!controller.signal.aborted) {
        const message = error instanceof Error ? error.message : 'Einstein could not provide a response.';
        setMessages((current) => [...current, { from: 'bot', text: message }]);
      }
    } finally {
      if (requestController.current === controller) requestController.current = null;
      setSending(false);
    }
  };

  const editUserMessage = (index: number) => {
    const message = messages[index];
    if (!message || message.from !== 'user' || sending) return;
    setMessages((current) => current.slice(0, index));
    setInput(message.text);
    setSelectedImage(message.imageDataUrl ? {
      file: new File([], 'attached-image', { type: message.imageDataUrl.match(/^data:([^;]+)/)?.[1] || 'image/png' }),
      dataUrl: message.imageDataUrl,
    } : null);
    setEditingIndex(index);
  };

  useEffect(() => () => requestController.current?.abort(), []);

  if (!isAuthenticated) return null;
  if (!hasChatAccess) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Subscribe to at least two paid features to use the AI Tutor. You currently have {activePaidFeatureCount} active {activePaidFeatureCount === 1 ? 'feature' : 'features'}.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/payment')}>Choose paid features</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', py: { xs: 2, md: 5 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="md" sx={{ height: { xs: 'calc(100vh - 100px)', md: 'calc(100vh - 150px)' } }}>
        <Paper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'common.white', background: 'linear-gradient(135deg, #087fce, #00a9e8)' }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <IconButton onClick={() => navigate(-1)} aria-label="Go back" sx={{ color: 'inherit' }}><ArrowBackIcon /></IconButton>
              <Avatar src={process.env.PUBLIC_URL + '/logo_with_bottom_text.svg'} alt="A3Tech AI Tutor" sx={{ width: 34, height: 34 }} />
              <Box><Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Einstein</Typography><Typography variant="caption">Full Chat Room</Typography></Box>
            </Stack>
            <IconButton onClick={() => setSettingsOpen(true)} aria-label="Open chat settings" sx={{ color: 'inherit' }}><SettingsIcon /></IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {messages.map((message, index) => (
                <Box key={`${message.from}-${index}`} sx={{ alignSelf: message.from === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">{message.from === 'user' ? 'You' : 'Einstein'}</Typography>
                    {message.from === 'user' && !sending && (
                      <IconButton size="small" onClick={() => editUserMessage(index)} aria-label="Edit message"><EditIcon fontSize="small" /></IconButton>
                    )}
                  </Stack>
                  <Paper sx={{ mt: 0.5, p: 1.5, backgroundColor: message.from === 'user' ? 'primary.main' : 'action.hover', color: message.from === 'user' ? 'primary.contrastText' : 'text.primary' }}>
                    {message.imageDataUrl && <Box component="img" src={message.imageDataUrl} alt="Attached image" sx={{ display: 'block', maxWidth: '100%', maxHeight: 280, mb: message.text ? 1 : 0, borderRadius: 1 }} />}
                    {message.from === 'bot' && message.html ? <Box dangerouslySetInnerHTML={{ __html: message.html }} /> : <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>}
                  </Paper>
                </Box>
              ))}
              {sending && <Typography color="text.secondary">Einstein is typing...</Typography>}
              <div ref={messagesEndRef} />
            </Stack>
          </Box>

          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {attachmentError && <Alert severity="error" onClose={() => setAttachmentError('')} sx={{ mb: 1 }}>{attachmentError}</Alert>}
            {selectedImage && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box component="img" src={selectedImage.dataUrl} alt="Image preview" sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>{selectedImage.file.name || 'Attached image'}</Typography>
                <IconButton size="small" onClick={() => setSelectedImage(null)} aria-label="Remove image"><CloseIcon /></IconButton>
              </Box>
            )}
            {editingIndex !== null && <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 0.5 }}>Editing message — sending replaces it and the following replies.</Typography>}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <input ref={imageInputRef} hidden type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageSelected} />
              <IconButton onClick={() => imageInputRef.current?.click()} disabled={sending} aria-label="Attach image"><ImageIcon /></IconButton>
              <TextField fullWidth size="small" label="Ask Einstein" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }} multiline maxRows={5} />
              {sending ? (
                <IconButton color="error" onClick={stopResponse} aria-label="Stop response"><StopIcon /></IconButton>
              ) : (
                <IconButton color="primary" onClick={() => send()} disabled={!input.trim() && !selectedImage} aria-label={editingIndex !== null ? 'Send edited message' : 'Send message'}><SendIcon /></IconButton>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
      <ChatSettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} conversationId={conversationId} />
    </Box>
  );
};

export default ChatRoom;
